"use client";

import { useEffect, useState } from "react";
import Link from "next/link";
import { useSearchParams } from "next/navigation";
import { useConvex } from "convex/react";
import { api } from "../../../convex/_generated/api";
import { Navbar } from "@/components/layout/Navbar";
import { Footer } from "@/components/layout/Footer";
import { usePageTitle } from "@/lib/usePageTitle";
import { type SubscriptionStatus } from "@/lib/billing/plans";

const POLL_TIMEOUT_MS = 30_000; // 30 seconds max polling
const POLL_INTERVAL_MS = 2_000;

export default function CheckoutSuccessPage() {
  usePageTitle("Payment Successful");
  const searchParams = useSearchParams();
  const sessionId = searchParams.get("session_id");
  const convex = useConvex();
  const [timedOut, setTimedOut] = useState(false);
  const [pollError, setPollError] = useState<string | null>(null);
  const [subscriptionStatus, setSubscriptionStatus] = useState<SubscriptionStatus>("none");

  // Poll Convex until webhook-sync marks user active/trialing.
  useEffect(() => {
    let active = true;
    let timer: ReturnType<typeof setTimeout> | null = null;
    const startedAt = Date.now();

    const poll = async () => {
      try {
        const user = await convex.query(api.users.getCurrentUser, {});
        if (!active) return;
        const nextStatus = (user?.subscriptionStatus ?? "none") as SubscriptionStatus;
        setSubscriptionStatus(nextStatus);
        setPollError(null);

        if (nextStatus === "active" || nextStatus === "trialing") {
          return;
        }
      } catch {
        if (!active) return;
        setPollError("We could not refresh subscription status yet. Retrying...");
      }

      if (Date.now() - startedAt >= POLL_TIMEOUT_MS) {
        if (active) {
          setTimedOut(true);
        }
        return;
      }

      timer = setTimeout(poll, POLL_INTERVAL_MS);
    };

    poll();

    return () => {
      active = false;
      if (timer) {
        clearTimeout(timer);
      }
    };
  }, [convex]);

  const isActive = subscriptionStatus === "active" || subscriptionStatus === "trialing";

  return (
    <div className="flex min-h-screen flex-col bg-background">
      <Navbar />

      <main className="flex flex-1 flex-col items-center justify-center px-4 py-16 text-center">
        {isActive ? (
          /* Subscription confirmed */
          <div className="mx-auto max-w-md space-y-6">
            <div className="flex items-center justify-center">
              <div className="flex h-16 w-16 items-center justify-center rounded-full bg-primary/10">
                <svg
                  className="h-8 w-8 text-primary"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={2}
                >
                  <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                </svg>
              </div>
            </div>

            <div className="space-y-2">
              <h1 className="text-3xl font-bold text-foreground">Payment Successful!</h1>
              <p className="text-muted">
                Your subscription is now active. You can start creating your server community.
              </p>
            </div>

            {sessionId && (
              <p className="text-xs text-muted/60">
                Session ID: {sessionId}
              </p>
            )}

            <div className="flex flex-col gap-3 sm:flex-row sm:justify-center">
              <Link
                href="/dashboard"
                className="inline-flex items-center justify-center rounded-md bg-primary px-6 py-3 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
              >
                Go to Dashboard
              </Link>
              <Link
                href="/dashboard/my-orgs/new"
                className="inline-flex items-center justify-center rounded-md border border-border bg-background px-6 py-3 text-sm font-medium text-foreground transition-colors hover:bg-accent"
              >
                Create Your Community
              </Link>
            </div>
          </div>
        ) : timedOut ? (
          /* Webhook took too long — show success with manual check link */
          <div className="mx-auto max-w-md space-y-6">
            <div className="flex items-center justify-center">
              <div className="flex h-16 w-16 items-center justify-center rounded-full bg-yellow-500/10">
                <svg
                  className="h-8 w-8 text-yellow-600"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={2}
                >
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01M12 3a9 9 0 100 18 9 9 0 000-18z" />
                </svg>
              </div>
            </div>

            <div className="space-y-2">
              <h1 className="text-3xl font-bold text-foreground">Payment Processing</h1>
              <p className="text-muted">
                Your payment was received but your subscription is still being activated.
                This usually takes a few seconds — please check your billing page.
              </p>
            </div>

            <div className="flex flex-col gap-3 sm:flex-row sm:justify-center">
              <Link
                href="/dashboard/settings/billing"
                className="inline-flex items-center justify-center rounded-md bg-primary px-6 py-3 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
              >
                Check Billing Status
              </Link>
              <Link
                href="/dashboard"
                className="inline-flex items-center justify-center rounded-md border border-border bg-background px-6 py-3 text-sm font-medium text-foreground transition-colors hover:bg-accent"
              >
                Go to Dashboard
              </Link>
            </div>
          </div>
        ) : (
          /* Polling — waiting for webhook */
          <div className="mx-auto max-w-md space-y-6">
            <div className="flex items-center justify-center">
              <div className="h-12 w-12 animate-spin rounded-full border-4 border-border border-t-primary" />
            </div>
            <div className="space-y-2">
              <h1 className="text-2xl font-bold text-foreground">Activating Your Subscription</h1>
              <p className="text-muted">
                Payment confirmed — activating your account now...
              </p>
              {pollError && (
                <p className="text-sm text-muted">{pollError}</p>
              )}
            </div>
          </div>
        )}
      </main>

      <Footer />
    </div>
  );
}
